import { createEmptyLocation, Location } from '@/model/Location';
import { createEmptyTopic, Topic } from '@/model/Topic';
import { Dictionary } from 'lodash';
import groupBy from 'lodash.groupby';
import uniqBy from 'lodash.uniqby';
import {
    EventFlow,
    EventFlowItem,
    EventFlowLocation,
    EventFlowTopic,
} from '@/model/event-flow/EventFlow';
import { isDateSameOrBefore, isDateTimeSameOrAfter } from '@/lib/date-util';
import { addMinutes, differenceInMinutes } from 'date-fns';

export type DragDirection = 'pending' | 'horizontal' | 'vertical';
export type MinutePositionRounding = 'floor' | 'ceil';

export function isDateTimeSameOrBeforeStart(
    dateTime: Date,
    startDateTime: Date,
): boolean {
    return isDateSameOrBefore(dateTime, startDateTime);
}

export function isDateTimeSameOrAfterEnd(
    dateTime: Date,
    endDateTime: Date,
): boolean {
    return isDateTimeSameOrAfter(dateTime, endDateTime);
}

export function isStartMovementOutOfBound(
    offsetY: number,
    startTime: Date,
    startDateTime: Date,
): boolean {
    return offsetY < 0 && isDateTimeSameOrBeforeStart(startTime, startDateTime);
}

export function isEndMovementOutOfBound(
    offsetY: number,
    endTime: Date,
    endDateTime: Date,
): boolean {
    return offsetY >= 0 && isDateTimeSameOrAfterEnd(endTime, endDateTime);
}

export function calculateNumberOfMovedMinutes(
    offsetY: number,
    minuteRowHeight: number,
): number {
    if (minuteRowHeight <= 0) {
        return 0;
    }

    return Math.round(offsetY / minuteRowHeight);
}

export function determineDragDirection(
    currentDirection: DragDirection,
    offsetX: number,
    offsetY: number,
    activationThreshold: number,
): DragDirection {
    if (currentDirection !== 'pending') {
        return currentDirection;
    }

    const absoluteOffsetX = Math.abs(offsetX);
    const absoluteOffsetY = Math.abs(offsetY);

    if (
        Math.max(absoluteOffsetX, absoluteOffsetY) < activationThreshold ||
        absoluteOffsetX === absoluteOffsetY
    ) {
        return 'pending';
    }

    return absoluteOffsetX > absoluteOffsetY ? 'horizontal' : 'vertical';
}

export function addMinutesToDate(dateTime: Date, minutes: number): Date {
    return addMinutes(dateTime, minutes);
}

export function clampDateTimeToRange(
    dateTime: Date,
    minDateTime: Date,
    maxDateTime: Date,
): Date {
    if (dateTime < minDateTime) {
        return minDateTime;
    }

    if (dateTime > maxDateTime) {
        return maxDateTime;
    }

    return dateTime;
}

export function clampStartDateTime(
    startDateTime: Date,
    minStartDateTime: Date,
    endDateTime: Date,
    step: number,
): Date {
    return clampDateTimeToRange(
        startDateTime,
        minStartDateTime,
        addMinutes(endDateTime, -step),
    );
}

export function clampEndDateTime(
    endDateTime: Date,
    startDateTime: Date,
    maxEndDateTime: Date,
    step: number,
): Date {
    return clampDateTimeToRange(
        endDateTime,
        addMinutes(startDateTime, step),
        maxEndDateTime,
    );
}

export function clampMovedDateRange(
    startDateTime: Date,
    endDateTime: Date,
    minStartDateTime: Date,
    maxEndDateTime: Date,
): { startedAt: Date; endedAt: Date } {
    const durationInMinutes = differenceInMinutes(endDateTime, startDateTime);

    if (startDateTime < minStartDateTime) {
        return {
            startedAt: minStartDateTime,
            endedAt: addMinutes(minStartDateTime, durationInMinutes),
        };
    }

    if (endDateTime > maxEndDateTime) {
        return {
            startedAt: addMinutes(maxEndDateTime, -durationInMinutes),
            endedAt: maxEndDateTime,
        };
    }

    return {
        startedAt: startDateTime,
        endedAt: endDateTime,
    };
}

export function calculateMinutePos(
    timeValue: Date,
    startDateTime: Date,
    durationInMinutes: number,
    rounding: MinutePositionRounding,
): number {
    const rawOffset =
        (timeValue.getTime() - startDateTime.getTime()) / (60 * 1000);
    const minuteOffset =
        rounding === 'ceil' ? Math.ceil(rawOffset) : Math.floor(rawOffset);
    const visibleMinuteOffset = Math.min(
        Math.max(minuteOffset, 0),
        durationInMinutes,
    );

    return visibleMinuteOffset + 1;
}

export function setFlow(eventFlowItems: EventFlowItem[] = []): EventFlow {
    eventFlowItems.forEach((eventFlowItem) => {
        if (!eventFlowItem?.location) {
            eventFlowItem.location = createEmptyLocation();
        }

        if (!eventFlowItem?.topic) {
            eventFlowItem.topic = createEmptyTopic();
        }
    });

    const itemsGroupByLocation: Dictionary<EventFlowItem[]> = groupBy(
        eventFlowItems,
        (eventFlowItem) => eventFlowItem?.location?.id,
    );

    const locations: Location[] = uniqBy(
        eventFlowItems.map((eventFlowItem) => {
            return eventFlowItem.location ?? createEmptyLocation();
        }),
        (location) => location?.id,
    );

    const eventFlowLocations = locations
        .map((location) => {
            const itemsByLocation: EventFlowItem[] =
                itemsGroupByLocation[location.id ?? 0];

            const itemsGroupByTopic: Dictionary<EventFlowItem[]> = groupBy(
                itemsByLocation,
                (eventFlowItem) => eventFlowItem?.topic?.id,
            );

            const topics: Topic[] = uniqBy(
                itemsByLocation.map((eventFlowItem) => {
                    return eventFlowItem.topic ?? createEmptyTopic();
                }),
                (topic) => topic?.id,
            );

            const eventFlowTopics: EventFlowTopic[] = topics
                .map((topic) => {
                    return {
                        ...topic,
                        items: itemsGroupByTopic[topic.id ?? 0],
                    } as EventFlowTopic;
                })
                .sort((a, b) => {
                    if (!a?.order || !b?.order) {
                        return 1;
                    }

                    return a.order > b.order ? 1 : -1;
                });

            return {
                ...location,
                eventFlowTopics,
            } as EventFlowLocation;
        })
        .sort((a, b) => {
            if (!a?.order || !b?.order) {
                return 1;
            }

            return a.order > b.order ? 1 : -1;
        });

    let index = 0;

    eventFlowLocations.forEach((eventFlowLocation) => {
        eventFlowLocation.startIndex = index;

        eventFlowLocation.eventFlowTopics.forEach((eventFlowTopic) => {
            eventFlowTopic.startIndex = index;
            eventFlowTopic.endIndex = eventFlowTopic.items.length + index;

            eventFlowTopic.items
                .sort((a, b) => {
                    if (a.startedAt.getTime() === b.startedAt.getTime()) {
                        return a.endedAt > b.endedAt ? 1 : -1;
                    }

                    return a.startedAt > b.startedAt ? 1 : -1;
                })
                .map((eventFlowItem) => {
                    eventFlowItem.index = index;
                    index += 1;

                    return eventFlowItem;
                });

            eventFlowTopic.borderIndex = index;
            index += 1;
        });

        eventFlowLocation.endIndex = index;
        eventFlowLocation.borderIndex = index;
        index += 1;
    });

    eventFlowLocations.forEach((eventFlowLocation) => {
        if (eventFlowLocation.id === 0) {
            eventFlowLocation.id = null;
        }

        eventFlowLocation.eventFlowTopics.forEach((eventFlowTopic) => {
            if (eventFlowTopic.id === 0) {
                eventFlowTopic.id = null;
            }

            eventFlowTopic.items.forEach((eventFlowItem) => {
                if (eventFlowItem?.location?.id === 0) {
                    eventFlowItem.location = undefined;
                }

                if (eventFlowItem?.topic?.id === 0) {
                    eventFlowItem.topic = undefined;
                }
            });
        });
    });

    return {
        lastIndex: index,
        eventFlowLocations,
    };
}

export function getEnumValueByEnumKey<T>(
    myEnum: Record<string, T>,
    enumKey: T,
): unknown | null {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumKey);
    return keys.length > 0 ? keys[0] : null;
}

export function getEnumKeyByEnumValue<T>(
    myEnum: Record<string, unknown>,
    enumValue: unknown,
): keyof T | null {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
    return keys.length > 0 ? (keys[0] as keyof T) : null;
}

export function setStartDateTime(startDateTime: Date, date: Date): Date {
    startDateTime.setFullYear(date.getFullYear());
    startDateTime.setDate(date.getDate());
    startDateTime.setMonth(date.getMonth());

    return startDateTime;
}

export function setEndDateTime(endDateTime: Date, date: Date): Date {
    endDateTime.setFullYear(date.getFullYear());
    endDateTime.setMonth(date.getMonth());

    if (endDateTime.getHours() === 0 && endDateTime.getMinutes() === 0) {
        endDateTime.setDate(date.getDate() + 1);
    } else {
        endDateTime.setDate(date.getDate());
    }

    return endDateTime;
}

export function getStartDate(startDate: Date | undefined): Date {
    if (!startDate) {
        return new Date();
    }
    const tmpStartDate = new Date(startDate);
    const currentDay = new Date();

    if (tmpStartDate.getTime() > currentDay.getTime()) {
        return tmpStartDate;
    }

    return currentDay;
}
